import type {
  AddDraftBranchStepRequest,
  DraftBranchAppendedRule,
  ProcessDraftStep,
  UpdateDraftStepRequest
} from '@bliks-io/contracts';
import { buildEmptyRuleInputs, getDecisionTableColumns, type EdgeDescriptor } from '@bliks-io/ui';
import { resolveCurrentLaneKey } from '../graph/resolveLane';
import {
  mintObjectIdHex,
  mintTempDraftStepId
} from '../../../../../api/mutations/useProcessDraft/shared';
import { addSourceToNodeInputs } from '../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers
} from '../../../../../api/mutations/helpers/placeholderStep';
import { addDraftBranchStepApi, addDraftStepApi } from '../../../../../api/persist/draft';
import { expandRuleIdToOutputGroup } from '../graph/expandRuleIdToOutputGroup';
import {
  currentSuccessorStatus,
  findStepByStableId,
  optimisticStartEventInput,
  resolvePosition,
  rulesPatchWithStatuses
} from './shared';
import { ensureNotTerminal } from './successorInvariant';
import type { EdgeActionDeps } from './types';

/**
 * Fork off the start event: create a new step already attached to the start
 * event, in one request and so one undo entry (the backend marks
 * first-step-ness — `previousStep = <startEventNodeId>` + the eventId input
 * link). The new step becomes an additional first step alongside the existing
 * one (or the draft's only one). `onCreated` gets the new step's stableStepId
 * as soon as the server returns it.
 */
export async function forkFromStart(
  startEventId: string,
  position: { x: number; y: number },
  deps: EdgeActionDeps,
  onCreated?: (stableStepId: string) => void
): Promise<void> {
  const { processId } = deps;

  const performAttach = async (): Promise<string> => {
    const tempId = mintTempDraftStepId();
    const reconcilers = envelopePlaceholderReconcilers(tempId);

    const envelope = await deps.runTransaction({
      optimistic: (prev) => ({
        ...prev,
        draft: { ...prev.draft, updatedAt: new Date() },
        steps: [
          ...prev.steps,
          buildPlaceholderStep({
            tempId,
            processId,
            draftId: prev.draft._id,
            inputs: [optimisticStartEventInput(deps, startEventId)]
          })
        ]
      }),
      optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
      persist: async () => {
        const created = await addDraftStepApi(processId, {
          position,
          attachStartEventId: startEventId
        });
        onCreated?.(String(created.createdNodeIds[0]));
        return created;
      },
      reconcile: reconcilers.reconcile,
      reconcileLayout: reconcilers.reconcileLayout
    });

    return String(envelope.createdNodeIds[0]);
  };

  await performAttach();
}

type DraftRules = NonNullable<UpdateDraftStepRequest['rules']>;

// The new rule gets its own blank `output` — mirroring the "Add rule" button
// in `DecisionTableDialog` — so it lands on a fresh handle on the decision
// node rather than joining the clicked rule's group. Its `inputs` seed the
// union of condition columns across the decision (shared `getDecisionTableColumns`)
// so the row slots into the existing table structure.
function buildAppendedRuleDraft(existingRules: DraftRules): Omit<DraftBranchAppendedRule, '_id'> {
  const inputs = buildEmptyRuleInputs(getDecisionTableColumns(existingRules));
  return {
    inputs,
    output: '',
    successors: {
      persons: [],
      orgFunctions: [],
      systems: [],
      externalContacts: [],
      successorStatus: ''
    }
  };
}

/**
 * Appends the rule and, as the server does, clears the ended marker of the
 * output group it joins: a blank output joins an ended blank-output group.
 */
function withAppendedRule(
  source: ProcessDraftStep,
  appendedRule: DraftBranchAppendedRule
): ProcessDraftStep {
  const withRule = {
    ...source,
    rules: [...(source.rules ?? []), appendedRule]
  } as ProcessDraftStep;
  const ended = expandRuleIdToOutputGroup(withRule.rules, appendedRule._id).filter((rid) =>
    ['NONE', 'UNCLEAR'].includes(currentSuccessorStatus(withRule, rid))
  );
  if (ended.length === 0) return withRule;
  const patch = rulesPatchWithStatuses(withRule, new Map(ended.map((rid) => [rid, ''])));
  return { ...withRule, ...(patch as object) } as ProcessDraftStep;
}

function findClickedRule(
  source: ProcessDraftStep | undefined,
  ruleId: string
): DraftRules[number] | undefined {
  const rules = source?.rules as DraftRules | undefined;
  return rules?.find((r) => r._id && String(r._id) === ruleId);
}

async function forkFromStep(
  sourceStepId: string,
  ruleId: string | undefined,
  position: { x: number; y: number },
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  const allSteps = deps.getCachedSteps();
  const source = findStepByStableId(allSteps, sourceStepId);
  const laneHint = source
    ? resolveCurrentLaneKey(source, allSteps, deps.getAssigneeLookup?.())
    : undefined;

  // When forking off a concrete rule, append a new rule row with a fresh
  // (blank) output. Its `_id` is minted here so M's edge carries the rule
  // binding before the server answers.
  const clickedRule = ruleId ? findClickedRule(source, ruleId) : undefined;
  const appendedRule: DraftBranchAppendedRule | undefined =
    clickedRule && source?.rules
      ? {
          ...buildAppendedRuleDraft(source.rules as DraftRules),
          _id: mintObjectIdHex()
        }
      : undefined;
  // Optimistic-only: the branch endpoint clears the ended marker of the handle
  // M's edge leaves in-transaction. With an appended rule that is the rule's
  // output group (staged below); the clicked output keeps its marker.
  if (!appendedRule) ensureNotTerminal(sourceStepId, ruleId, deps);
  const connectRuleId = appendedRule ? appendedRule._id : ruleId;
  const ruleIdsForEdge = connectRuleId ? [connectRuleId] : undefined;

  const tempId = mintTempDraftStepId();
  const reconcilers = envelopePlaceholderReconcilers(tempId);
  const body: AddDraftBranchStepRequest = {
    anchor: {
      kind: 'after',
      sourceStableStepId: sourceStepId,
      ...(ruleId ? { ruleId } : {}),
      ...(appendedRule ? { appendedRule } : {})
    },
    position,
    ...(laneHint ? { laneKey: laneHint } : {})
  };

  await deps.runTransaction({
    optimistic: (prev) => {
      const stepsWithRule = appendedRule
        ? prev.steps.map((s) =>
            String(s.stableStepId) === sourceStepId ? withAppendedRule(s, appendedRule) : s
          )
        : prev.steps;
      const placeholder = addSourceToNodeInputs(
        buildPlaceholderStep({
          tempId,
          processId,
          draftId: prev.draft._id,
          laneKey: laneHint
        }),
        sourceStepId,
        ruleIdsForEdge
      );
      return {
        ...prev,
        draft: { ...prev.draft, updatedAt: new Date() },
        steps: [...stepsWithRule, placeholder]
      };
    },
    optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
    persist: () => addDraftBranchStepApi(processId, body),
    reconcile: reconcilers.reconcile,
    reconcileLayout: reconcilers.reconcileLayout
  });
}

export async function performForkFromSource(
  edge: EdgeDescriptor,
  deps: EdgeActionDeps
): Promise<void> {
  const { source } = edge;
  const position = resolvePosition(edge, deps);

  if (source.kind === 'start') {
    if (source.eventId) {
      await forkFromStart(source.eventId, position, deps);
    }
    return;
  }
  if (source.stepId) {
    await forkFromStep(source.stepId, edge.ruleId, position, deps);
  }
}
